/**
 * One-time in-app announcement modal. Renders a server-authored block list over the
 * shared BaseModal shell, in a scroll-safe card that always carries a close control.
 * See specs/in-app-announcements/spec.md §7.
 */

import React, { useMemo } from 'react';
import { ScrollView, View } from 'react-native';
import { BaseModal } from '../../ui/BaseModal/BaseModal';
import { ANNOUNCEMENT_TEST_IDS } from '../../../src/announcements/constants/announcements.constants';
import type {
  AnnouncementInAppAction,
  RawAnnouncementBlock,
} from '../../../src/announcements/types/announcements.types';
import { AnnouncementBlocks, AnnouncementCloseButton } from './AnnouncementBlocks';
import { getAnnouncementModalStyles } from './AnnouncementModal.styles';
import { ANNOUNCEMENT_MODAL_COPY } from './announcementModal.constants';

export interface AnnouncementModalProps {
  visible: boolean;
  blocks: RawAnnouncementBlock[];
  onDismiss: () => void;
  /** Invoked when a button carries a recognized `inAppAction` (e.g. opening the account switcher). */
  onInAppAction?: (action: AnnouncementInAppAction) => void;
}

export function AnnouncementModal({
  visible,
  blocks,
  onDismiss,
  onInAppAction,
}: AnnouncementModalProps): React.ReactElement {
  const styles = useMemo(() => getAnnouncementModalStyles(), []);

  return (
    <BaseModal
      isVisible={visible}
      onDismiss={onDismiss}
      testID={ANNOUNCEMENT_TEST_IDS.MODAL}
      contentTestID={ANNOUNCEMENT_TEST_IDS.CARD}
      accessibilityLabel={ANNOUNCEMENT_MODAL_COPY.A11Y_TITLE}
    >
      <View style={styles.cardWrap}>
        <View style={styles.card}>
          <AnnouncementCloseButton
            onDismiss={onDismiss}
            styles={styles}
            a11yLabel={ANNOUNCEMENT_MODAL_COPY.A11Y_CLOSE}
          />
          <ScrollView
            style={styles.scroll}
            showsVerticalScrollIndicator={false}
            contentContainerStyle={{ paddingTop: 4 }}
          >
            <AnnouncementBlocks
              blocks={blocks}
              onDismiss={onDismiss}
              onInAppAction={onInAppAction}
              styles={styles}
            />
          </ScrollView>
        </View>
      </View>
    </BaseModal>
  );
}
